import React from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { ThemeProvider } from "@mui/material/styles";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import { theme } from "../../utils/themeCreator";
import { AssignSitesCamerasPicker } from "./AssignSitesCamerasPicker";
import { fetchTalkDownAssignSites } from "./talkDownAssignFetchers";
import { useTalkDownMessagesStore } from "./talkDownMessagesStore";
import {
  formatTalkDownDuration,
  TalkDownAssignSite,
  TalkDownAssignment,
  TalkDownMessage,
} from "./talkDownMessageTypes";
import { useAudioRecorder } from "../../common/TalkDown/useAudioRecorder";

const formatRecordedDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

const formatCamerasCell = (a: TalkDownAssignment): string => {
  if (a.mode === "entire_site") return "All cameras";
  return a.cameraNames.join(", ");
};

export const TalkDownMessageDetail = ({
  message,
  onClose,
}: {
  message: TalkDownMessage | null;
  onClose: () => void;
}) => {
  const updateMessage = useTalkDownMessagesStore((s) => s.updateMessage);
  const setAssignments = useTalkDownMessagesStore((s) => s.setAssignments);
  const removeAssignment = useTalkDownMessagesStore((s) => s.removeAssignment);
  const deleteMessage = useTalkDownMessagesStore((s) => s.deleteMessage);
  const live = useTalkDownMessagesStore((s) =>
    message ? s.messages.find((m) => m.id === message.id) : undefined
  );

  const [editingAssignments, setEditingAssignments] = React.useState(false);
  const [editingSiteId, setEditingSiteId] = React.useState<
    string | number | null
  >(null);
  const [draftAssignments, setDraftAssignments] = React.useState<
    TalkDownAssignment[]
  >([]);
  const [assignSearch, setAssignSearch] = React.useState("");
  const [assignSites, setAssignSites] = React.useState<TalkDownAssignSite[]>(
    []
  );
  const [assignSitesLoading, setAssignSitesLoading] = React.useState(false);
  const [assignSitesError, setAssignSitesError] = React.useState<string | null>(
    null
  );
  const [currentTime, setCurrentTime] = React.useState(0);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const elapsedRef = React.useRef(0);
  const loadedAssignGroupRef = React.useRef<string | null>(null);

  const row = live ?? message;

  const recorder = useAudioRecorder({
    maxSeconds: 30,
    onRecorded: (blob) => {
      if (!row) return;
      const url = URL.createObjectURL(blob);
      if (row.audioUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(row.audioUrl);
      }
      void updateMessage(row.id, {
        audioUrl: url,
        durationSeconds: Math.max(1, elapsedRef.current || 1),
        recordedAt: new Date().toISOString(),
      });
    },
  });

  React.useEffect(() => {
    elapsedRef.current = recorder.elapsedSeconds;
  }, [recorder.elapsedSeconds]);

  const resetAssignEditor = () => {
    setEditingAssignments(false);
    setEditingSiteId(null);
    setDraftAssignments([]);
    setAssignSearch("");
  };

  // Dialog stays mounted when closed; clear edit mode so reopen is not stuck editing.
  React.useEffect(() => {
    setEditingAssignments(false);
    setEditingSiteId(null);
    setDraftAssignments([]);
    setAssignSearch("");
    setAssignSites([]);
    setAssignSitesError(null);
    setAssignSitesLoading(false);
    loadedAssignGroupRef.current = null;
    setCurrentTime(0);
  }, [message?.id]);

  React.useEffect(() => {
    if (!editingAssignments || !row?.groupId) return;
    const key = String(row.groupId);
    if (loadedAssignGroupRef.current === key) return;

    let cancelled = false;
    setAssignSitesLoading(true);
    setAssignSitesError(null);
    fetchTalkDownAssignSites(row.groupId)
      .then((sites) => {
        if (cancelled) return;
        setAssignSites(sites);
        loadedAssignGroupRef.current = key;
      })
      .catch(() => {
        if (cancelled) return;
        setAssignSites([]);
        setAssignSitesError("Could not load sites for this group.");
        loadedAssignGroupRef.current = null;
      })
      .finally(() => {
        if (!cancelled) setAssignSitesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [editingAssignments, row?.groupId]);

  if (!row) return null;

  const openAssignEditor = () => {
    setDraftAssignments(row.assignments);
    setAssignSearch("");
    setEditingSiteId(null);
    setEditingAssignments(true);
  };

  const openSiteCameraEditor = (siteId: string | number) => {
    setDraftAssignments(row.assignments);
    setAssignSearch("");
    setEditingSiteId(siteId);
    setEditingAssignments(true);
  };

  const closeAssignEditor = (save: boolean) => {
    if (save) {
      void setAssignments(row.id, draftAssignments);
    }
    resetAssignEditor();
  };

  const handleClose = () => {
    resetAssignEditor();
    onClose();
  };

  const handleDownload = () => {
    if (!row.audioUrl) return;
    const a = document.createElement("a");
    a.href = row.audioUrl;
    a.download = `${row.name.replace(/\s+/g, "_").toLowerCase()}.webm`;
    a.click();
  };

  return (
    <Dialog
      open={Boolean(message)}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { fontFamily: "Mulish" } }}
    >
      <DialogTitle
        sx={{
          fontFamily: "Mulish",
          fontWeight: 800,
          pr: 6,
          color: "#001943",
        }}
      >
        {row.name}
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <p className="mb-3 font-mulish text-[13px] text-gray-600">
          {row.groupName} · Recorded by {row.recordedBy} on{" "}
          {formatRecordedDate(row.recordedAt)} · Played {row.playCount} times
        </p>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          {row.audioUrl ? (
            <>
              <audio
                ref={audioRef}
                controls
                src={row.audioUrl}
                className="max-w-full flex-1"
                onTimeUpdate={() =>
                  setCurrentTime(audioRef.current?.currentTime ?? 0)
                }
                onPlay={() =>
                  void updateMessage(row.id, { playCount: row.playCount + 1 })
                }
              />
              <span className="font-mulish text-[13px] text-gray-600">
                {formatTalkDownDuration(currentTime)} /{" "}
                {formatTalkDownDuration(row.durationSeconds)}
              </span>
            </>
          ) : (
            <p className="font-mulish text-[13px] text-gray-500">
              No audio attached. Hold to re-record to add audio.
            </p>
          )}
        </div>

        <div className="mb-6 flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={`inline-flex h-10 items-center justify-center gap-1.5 rounded-md px-3 font-mulish text-[13px] font-semibold text-white ${
              recorder.recording ? "bg-[#B3261E]" : "bg-actuate-orange"
            }`}
            aria-label={
              recorder.recording ? "Stop re-record" : "Hold to re-record"
            }
            title={
              recorder.recording ? "Release to stop" : "Hold to re-record"
            }
            onPointerDown={(e) => {
              if (e.button !== 0) return;
              e.preventDefault();
              recorder.start();
            }}
            onPointerUp={() => recorder.stop()}
            onPointerLeave={() => {
              if (recorder.recording) recorder.stop();
            }}
            style={{ touchAction: "none" }}
          >
            <FiberManualRecordIcon
              sx={{ fontSize: 14 }}
              className={recorder.recording ? "animate-pulse" : undefined}
            />
            {recorder.recording
              ? `Recording ${formatTalkDownDuration(recorder.elapsedSeconds)}`
              : "Hold to re-record"}
          </button>
          <ThemeProvider theme={theme}>
            <Button
              type="button"
              color="primary"
              disabled={!row.audioUrl}
              onClick={handleDownload}
              startIcon={<DownloadIcon />}
              aria-label="Download"
              className="hover:bg-[#E67300]"
            >
              Download
            </Button>
            <Button
              type="button"
              onClick={() => {
                void deleteMessage(row.id).then(() => handleClose());
              }}
              aria-label="Delete message"
              sx={{
                color: "#B3261E",
                fontFamily: "Mulish",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "rgba(179, 38, 30, 0.08)",
                },
              }}
            >
              Delete message
            </Button>
          </ThemeProvider>
        </div>

        <div className="mb-2 flex items-center justify-between gap-3">
          <h3 className="font-mulish text-[16px] font-bold text-actuate-blue">
            Assigned to
          </h3>
          <OrangeTextButton
            text={
              editingAssignments ? "Done" : "+ Add sites or cameras"
            }
            disabled={false}
            onClick={() => {
              if (editingAssignments) {
                closeAssignEditor(true);
              } else {
                openAssignEditor();
              }
            }}
          />
        </div>

        {editingAssignments ? (
          assignSitesLoading ? (
            <div className="flex items-center gap-3 font-mulish text-[13px] text-gray-500">
              <CircularProgress size={22} sx={{ color: "#FF8400" }} />
              <span>Loading sites and cameras…</span>
            </div>
          ) : assignSitesError ? (
            <p className="font-mulish text-[13px] text-[#B3261E]">
              {assignSitesError}
            </p>
          ) : (
            <AssignSitesCamerasPicker
              sites={assignSites}
              search={assignSearch}
              onSearchChange={setAssignSearch}
              assignments={draftAssignments}
              onChange={setDraftAssignments}
              camerasOnlySiteId={editingSiteId}
            />
          )
        ) : (
          <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ fontFamily: "Mulish", fontWeight: 700, width: "42%" }}
                >
                  Site
                </TableCell>
                <TableCell
                  sx={{ fontFamily: "Mulish", fontWeight: 700, width: "42%" }}
                >
                  Cameras
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: "Mulish",
                    fontWeight: 700,
                    width: "16%",
                    textAlign: "right",
                  }}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {row.assignments.map((a) => (
                <TableRow key={String(a.siteId)}>
                  <TableCell
                    sx={{
                      fontFamily: "Mulish",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {a.siteName}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Mulish",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {formatCamerasCell(a)}
                  </TableCell>
                  <TableCell
                    sx={{ fontFamily: "Mulish", whiteSpace: "nowrap" }}
                    align="right"
                  >
                    <div className="inline-flex items-center justify-end gap-1">
                      <Tooltip title="Edit cameras">
                        <IconButton
                          size="small"
                          aria-label="Edit cameras"
                          onClick={() => openSiteCameraEditor(a.siteId)}
                          sx={{ color: "#FF8400" }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <IconButton
                          size="small"
                          aria-label="Remove"
                          onClick={() =>
                            void removeAssignment(row.id, a.siteId)
                          }
                          sx={{
                            color: "#B3261E",
                            "&:hover": {
                              backgroundColor: "rgba(179, 38, 30, 0.08)",
                            },
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!row.assignments.length && (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    sx={{ fontFamily: "Mulish", color: "#6b7280" }}
                  >
                    Unassigned
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
};
